import React, { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Heart,
  Activity,
  Trophy,
  BookOpen,
  LayoutDashboard,
  AlertTriangle,
  RefreshCw,
  Plus,
  ExternalLink,
  TrendingUp,
  TrendingDown,
  Minus,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Progress } from "../components/ui/progress";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { apiClient } from "../services/api";
import { toast } from "sonner";

const TABS = [
  { id: "checkin",    icon: Heart,          section: "myWellbeing" },
  { id: "challenges", icon: Trophy,         section: "myWellbeing" },
  { id: "resources",  icon: BookOpen,       section: "myWellbeing" },
  { id: "dashboard",  icon: LayoutDashboard, section: "manager" },
];

const MOOD_EMOJIS = ["", "😞", "😕", "😐", "🙂", "😄"];
const ENERGY_EMOJIS = ["", "😴", "🥱", "😑", "⚡", "🚀"];
const STRESS_EMOJIS = ["", "🤯", "😰", "😓", "😌", "🧘"];

const SCORE_KEYS = ["", "one", "two", "three", "four", "five"];

const CAT_COLORS = {
  physical:      "bg-green-100 text-green-800",
  mental:        "bg-purple-100 text-purple-800",
  social:        "bg-blue-100 text-blue-800",
  nutrition:     "bg-orange-100 text-orange-800",
  sleep:         "bg-indigo-100 text-indigo-800",
  mental_health: "bg-purple-100 text-purple-800",
  financial:     "bg-yellow-100 text-yellow-800",
  legal:         "bg-gray-100 text-gray-700",
  family:        "bg-red-100 text-red-800",
};

const categoryLabel = (category, t) =>
  t(`wellbeingHub.categories.${category}`, { defaultValue: category?.replace(/_/g, " ") || "" });

export default function WellbeingHub() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get("tab") || "checkin";
  const setTab = (tab) => setSearchParams({ tab });
  const sections = [...new Set(TABS.map((t) => t.section))];

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Heart className="h-6 w-6 text-rose-500" />
          {t("wellbeingHub.title")}
        </h1>
        <p className="text-muted-foreground text-sm mt-1">
          {t("wellbeingHub.subtitle")}
        </p>
      </div>

      <div className="border-b">
        {sections.map((section) => (
          <div key={section} className="inline-block mr-6 mb-0">
            <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mr-3">
              {t(`wellbeingHub.sections.${section}`)}
            </span>
            {TABS.filter((t) => t.section === section).map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setTab(tab.id)}
                  className={`inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium border-b-2 transition-colors mr-1 ${
                    activeTab === tab.id
                      ? "border-primary text-primary"
                      : "border-transparent text-muted-foreground hover:text-foreground"
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  {t(`wellbeingHub.tabs.${tab.id}`)}
                </button>
              );
            })}
          </div>
        ))}
      </div>

      <div>
        {activeTab === "checkin"    && <CheckinTab />}
        {activeTab === "challenges" && <ChallengesTab />}
        {activeTab === "resources"  && <ResourcesTab />}
        {activeTab === "dashboard"  && <DashboardTab />}
      </div>
    </div>
  );
}

// ── Check-in ──────────────────────────────────────────────────────────────────

function CheckinTab() {
  const { t } = useTranslation();
  const [today, setToday] = useState(null);
  const [history, setHistory] = useState([]);
  const [burnout, setBurnout] = useState(null);
  const [form, setForm] = useState({ mood_score: 3, energy_score: 3, stress_score: 3, note: "", is_anonymous: false });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    const [todayRes, histRes, burnoutRes] = await Promise.all([
      apiClient.get("v1/wellbeing/checkins/today").catch(() => ({ data: null })),
      apiClient.get("v1/wellbeing/checkins/me?days=30").catch(() => ({ data: [] })),
      apiClient.get("v1/wellbeing/burnout/me").catch(() => ({ data: null })),
    ]);
    setToday(todayRes.data);
    setHistory(histRes.data || []);
    setBurnout(burnoutRes.data);
    if (todayRes.data) {
      setForm({
        mood_score: todayRes.data.mood_score,
        energy_score: todayRes.data.energy_score,
        stress_score: todayRes.data.stress_score,
        note: todayRes.data.note || "",
        is_anonymous: todayRes.data.is_anonymous || false,
      });
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    setSaving(true);
    try {
      await apiClient.post("v1/wellbeing/checkins", form);
      toast.success(today ? t("wellbeingHub.checkin.updated") : t("wellbeingHub.checkin.saved"));
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || t("wellbeingHub.common.error"));
    } finally {
      setSaving(false);
    }
  };

  const burnoutColor = (level) => ({
    low:     "text-green-600 bg-green-50 border-green-200",
    medium:  "text-yellow-600 bg-yellow-50 border-yellow-200",
    high:    "text-red-600 bg-red-50 border-red-200",
    unknown: "text-muted-foreground bg-muted border-border",
  }[level] || "text-muted-foreground bg-muted");

  return (
    <div className="space-y-6 max-w-xl">
      {/* Check-in card */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center justify-between">
            <span>{t("wellbeingHub.checkin.prompt")}</span>
            {today && <Badge variant="outline" className="text-xs text-green-600">{t("wellbeingHub.checkin.alreadyFilled")}</Badge>}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-5">
          <ScoreSlider
            label={t("wellbeingHub.checkin.mood")}
            emojis={MOOD_EMOJIS}
            labelsKey="moodLabels"
            value={form.mood_score}
            onChange={(v) => setForm((f) => ({ ...f, mood_score: v }))}
            color="rose"
          />
          <ScoreSlider
            label={t("wellbeingHub.checkin.energy")}
            emojis={ENERGY_EMOJIS}
            labelsKey="energyLabels"
            value={form.energy_score}
            onChange={(v) => setForm((f) => ({ ...f, energy_score: v }))}
            color="amber"
          />
          <ScoreSlider
            label={t("wellbeingHub.checkin.stress")}
            emojis={STRESS_EMOJIS}
            labelsKey="stressLabels"
            value={form.stress_score}
            onChange={(v) => setForm((f) => ({ ...f, stress_score: v }))}
            color="blue"
          />

          <div>
            <label className="text-sm font-medium mb-1 block">{t("wellbeingHub.checkin.noteLabel")}</label>
            <Textarea
              placeholder={t("wellbeingHub.checkin.notePlaceholder")}
              value={form.note}
              onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))}
              rows={2}
            />
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="anon"
              checked={form.is_anonymous}
              onChange={(e) => setForm((f) => ({ ...f, is_anonymous: e.target.checked }))}
              className="accent-primary"
            />
            <label htmlFor="anon" className="text-sm text-muted-foreground">{t("wellbeingHub.checkin.anonymous")}</label>
          </div>

          <Button onClick={submit} disabled={saving} className="w-full">
            {saving ? t("wellbeingHub.checkin.saving") : today ? t("wellbeingHub.checkin.updateCta") : t("wellbeingHub.checkin.submitCta")}
          </Button>
        </CardContent>
      </Card>

      {/* Burnout risk */}
      {burnout && burnout.risk_level !== "unknown" && (
        <Card className={`border ${burnoutColor(burnout.risk_level)}`}>
          <CardContent className="py-4 px-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-semibold text-sm">{t("wellbeingHub.checkin.burnoutScore")}</p>
                <p className="text-xs mt-0.5">{burnout.risk_label}</p>
              </div>
              <div className="text-right">
                <p className="text-2xl font-bold">{burnout.risk_score}</p>
                <p className="text-xs opacity-70">{t("wellbeingHub.checkin.outOf100")}</p>
              </div>
            </div>
            {burnout.factors?.length > 0 && (
              <div className="mt-3 space-y-1">
                {burnout.factors.map((f, i) => (
                  <p key={i} className="text-xs flex items-center gap-1">
                    <AlertTriangle className="h-3 w-3 flex-shrink-0" /> {f}
                  </p>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* 30-day sparkline */}
      {history.length > 1 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">{t("wellbeingHub.checkin.trend30")}</CardTitle>
          </CardHeader>
          <CardContent>
            <MoodSparkline data={history} />
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function ScoreSlider({ label, emojis, labelsKey, value, onChange, color }) {
  const { t } = useTranslation();
  const colorMap = {
    rose:  "accent-rose-500",
    amber: "accent-amber-500",
    blue:  "accent-blue-500",
  };
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <label className="text-sm font-medium">{label}</label>
        <span className="text-lg">{emojis[value]}</span>
      </div>
      <input
        type="range"
        min={1}
        max={5}
        value={value}
        onChange={(e) => onChange(parseInt(e.target.value))}
        className={`w-full h-2 ${colorMap[color]} cursor-pointer`}
      />
      <div className="flex justify-between mt-1">
        {[1, 2, 3, 4, 5].map((n) => (
          <span key={n} className={`text-xs ${n === value ? "text-foreground font-medium" : "text-muted-foreground"}`}>
            {n}
          </span>
        ))}
      </div>
      <p className="text-xs text-center text-muted-foreground mt-0.5">{t(`wellbeingHub.checkin.${labelsKey}.${SCORE_KEYS[value]}`)}</p>
    </div>
  );
}

function MoodSparkline({ data }) {
  const { t } = useTranslation();
  const max = 5, h = 48, w = 280;
  const pts = data.map((d, i) => ({
    x: (i / (data.length - 1)) * (w - 20) + 10,
    y: h - (d.mood_score / max) * (h - 8) - 4,
    mood: d.mood_score,
    date: d.checkin_date,
  }));

  const path = pts.map((p, i) => `${i === 0 ? "M" : "L"}${p.x},${p.y}`).join(" ");
  const avg = Math.round(data.reduce((s, d) => s + d.mood_score, 0) / data.length * 10) / 10;

  return (
    <div>
      <div className="flex items-center gap-3 mb-2">
        <span className="text-xs text-muted-foreground">{t("wellbeingHub.checkin.avgMood")} </span>
        <span className="font-semibold text-sm">{avg}/5</span>
        <span className="text-lg">{MOOD_EMOJIS[Math.round(avg)]}</span>
      </div>
      <svg viewBox={`0 0 ${w} ${h}`} className="w-full">
        <path d={path} fill="none" stroke="hsl(var(--primary))" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        {pts.map((p, i) => (
          <circle key={i} cx={p.x} cy={p.y} r="2.5" fill="hsl(var(--primary))" />
        ))}
      </svg>
      <div className="flex justify-between text-xs text-muted-foreground mt-1">
        <span>{data[0]?.checkin_date}</span>
        <span>{data[data.length - 1]?.checkin_date}</span>
      </div>
    </div>
  );
}

// ── Challenges ────────────────────────────────────────────────────────────────

function ChallengesTab() {
  const { t } = useTranslation();
  const [challenges, setChallenges] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", category: "mental", start_date: "", end_date: "", points: 100 });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    const [chRes] = await Promise.all([
      apiClient.get("v1/wellbeing/challenges"),
      apiClient.get("v1/wellbeing/checkins/today").catch(() => ({ data: null })),
    ]);
    setChallenges(chRes.data || []);
  }, []);

  useEffect(() => { load(); }, [load]);

  const joinChallenge = async (id) => {
    try {
      await apiClient.post(`v1/wellbeing/challenges/${id}/join`);
      toast.success(t("wellbeingHub.challenges.joined"));
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || t("wellbeingHub.common.error"));
    }
  };

  const updateProgress = async (id, pct) => {
    try {
      await apiClient.patch(`v1/wellbeing/challenges/${id}/progress`, { progress_pct: pct });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || t("wellbeingHub.challenges.joinFirst"));
    }
  };

  const createChallenge = async () => {
    if (!form.title || !form.start_date || !form.end_date) { toast.error(t("wellbeingHub.challenges.requiredFields")); return; }
    setSaving(true);
    try {
      await apiClient.post("v1/wellbeing/challenges", { ...form, points: parseInt(form.points) });
      toast.success(t("wellbeingHub.challenges.created"));
      setShowForm(false);
      setForm({ title: "", description: "", category: "mental", start_date: "", end_date: "", points: 100 });
      load();
    } catch { toast.error(t("wellbeingHub.challenges.createError")); }
    finally { setSaving(false); }
  };

  const CATEGORIES = ["physical", "mental", "social", "nutrition", "sleep"];

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <p className="text-sm text-muted-foreground">{t("wellbeingHub.challenges.count", { count: challenges.length })}</p>
        <Button size="sm" onClick={() => setShowForm(!showForm)}>
          <Plus className="h-4 w-4 mr-1" /> {t("wellbeingHub.challenges.new")}
        </Button>
      </div>

      {showForm && (
        <Card className="border-primary/30">
          <CardContent className="pt-4 space-y-3">
            <Input placeholder={t("wellbeingHub.challenges.titlePlaceholder")} value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} />
            <Textarea placeholder={t("wellbeingHub.challenges.descriptionPlaceholder")} value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} rows={2} />
            <div className="grid grid-cols-2 gap-2">
              <Select value={form.category} onValueChange={(v) => setForm((f) => ({ ...f, category: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{categoryLabel(c, t)}</SelectItem>)}
                </SelectContent>
              </Select>
              <Input type="number" placeholder={t("wellbeingHub.challenges.pointsPlaceholder")} value={form.points} onChange={(e) => setForm((f) => ({ ...f, points: e.target.value }))} />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-xs mb-1 block">{t("wellbeingHub.challenges.start")}</label>
                <Input type="date" value={form.start_date} onChange={(e) => setForm((f) => ({ ...f, start_date: e.target.value }))} />
              </div>
              <div>
                <label className="text-xs mb-1 block">{t("wellbeingHub.challenges.end")}</label>
                <Input type="date" value={form.end_date} onChange={(e) => setForm((f) => ({ ...f, end_date: e.target.value }))} />
              </div>
            </div>
            <div className="flex gap-2">
              <Button size="sm" onClick={createChallenge} disabled={saving}>{saving ? t("wellbeingHub.challenges.creating") : t("wellbeingHub.challenges.createCta")}</Button>
              <Button size="sm" variant="outline" onClick={() => setShowForm(false)}>{t("wellbeingHub.common.cancel")}</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {challenges.length === 0 && (
        <Card>
          <CardContent className="py-10 text-center text-muted-foreground">
            <Trophy className="h-8 w-8 mx-auto mb-2 opacity-30" />
            <p>{t("wellbeingHub.challenges.empty")}</p>
          </CardContent>
        </Card>
      )}

      <div className="grid gap-3 md:grid-cols-2">
        {challenges.map((ch) => {
          const catCls = CAT_COLORS[ch.category] || "bg-gray-100 text-gray-700";
          const participants = ch.participants || [];
          const daysLeft = Math.max(0, Math.ceil((new Date(ch.end_date) - new Date()) / 86400000));

          return (
            <Card key={ch.id}>
              <CardContent className="py-4 px-4">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1">
                    <p className="font-semibold text-sm">{ch.title}</p>
                    {ch.description && <p className="text-xs text-muted-foreground mt-0.5">{ch.description}</p>}
                    <div className="flex items-center gap-2 mt-2 flex-wrap">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${catCls}`}>{categoryLabel(ch.category, t)}</span>
                      <span className="text-xs text-muted-foreground">{t("wellbeingHub.challenges.points", { points: ch.points })}</span>
                      <span className="text-xs text-muted-foreground">{t("wellbeingHub.challenges.daysLeft", { count: daysLeft })}</span>
                      <span className="text-xs text-muted-foreground">{t("wellbeingHub.challenges.participants", { count: participants.length })}</span>
                    </div>
                  </div>
                </div>
                <div className="mt-3 flex items-center gap-2">
                  <Button size="sm" variant="outline" onClick={() => joinChallenge(ch.id)} className="text-xs">
                    {t("wellbeingHub.challenges.join")}
                  </Button>
                  <div className="flex-1 flex items-center gap-2">
                    <input
                      type="range" min={0} max={100} step={10}
                      defaultValue={0}
                      onChange={(e) => updateProgress(ch.id, parseInt(e.target.value))}
                      className="flex-1 h-1 accent-primary cursor-pointer"
                    />
                    <span className="text-xs text-muted-foreground w-8">{t("wellbeingHub.challenges.progressShort")}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}

// ── EAP Resources ─────────────────────────────────────────────────────────────

function ResourcesTab() {
  const { t } = useTranslation();
  const [resources, setResources] = useState([]);
  const [catFilter, setCatFilter] = useState("all");
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", url: "", category: "mental_health" });
  const [saving, setSaving] = useState(false);

  const CATEGORIES = ["mental_health", "financial", "legal", "family", "nutrition", "physical", "sleep"];

  const load = useCallback(async () => {
    const res = await apiClient.get("v1/wellbeing/resources");
    setResources(res.data || []);
  }, []);

  useEffect(() => { load(); }, [load]);

  const createResource = async () => {
    if (!form.title.trim()) { toast.error(t("wellbeingHub.resources.titleRequired")); return; }
    setSaving(true);
    try {
      await apiClient.post("v1/wellbeing/resources", form);
      toast.success(t("wellbeingHub.resources.added"));
      setShowForm(false);
      setForm({ title: "", description: "", url: "", category: "mental_health" });
      load();
    } catch { toast.error(t("wellbeingHub.common.error")); }
    finally { setSaving(false); }
  };

  const filtered = catFilter && catFilter !== "all" ? resources.filter((r) => r.category === catFilter) : resources;
  const grouped = {};
  filtered.forEach((r) => {
    if (!grouped[r.category]) grouped[r.category] = [];
    grouped[r.category].push(r);
  });

  return (
    <div className="space-y-4 max-w-2xl">
      <div className="flex gap-3 items-center">
        <Select value={catFilter} onValueChange={setCatFilter}>
          <SelectTrigger className="w-48">
            <SelectValue placeholder={t("wellbeingHub.resources.allCategories")} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("wellbeingHub.resources.allCategories")}</SelectItem>
            {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{categoryLabel(c, t)}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button size="sm" onClick={() => setShowForm(!showForm)}>
          <Plus className="h-4 w-4 mr-1" /> {t("wellbeingHub.resources.add")}
        </Button>
      </div>

      {showForm && (
        <Card className="border-primary/30">
          <CardContent className="pt-4 space-y-3">
            <Input placeholder={t("wellbeingHub.resources.titlePlaceholder")} value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} />
            <Textarea placeholder={t("wellbeingHub.resources.descriptionPlaceholder")} value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} rows={2} />
            <Input placeholder={t("wellbeingHub.resources.urlPlaceholder")} value={form.url} onChange={(e) => setForm((f) => ({ ...f, url: e.target.value }))} />
            <Select value={form.category} onValueChange={(v) => setForm((f) => ({ ...f, category: v }))}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{categoryLabel(c, t)}</SelectItem>)}
              </SelectContent>
            </Select>
            <div className="flex gap-2">
              <Button size="sm" onClick={createResource} disabled={saving}>{saving ? t("wellbeingHub.resources.adding") : t("wellbeingHub.resources.addCta")}</Button>
              <Button size="sm" variant="outline" onClick={() => setShowForm(false)}>{t("wellbeingHub.common.cancel")}</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {Object.entries(grouped).map(([cat, items]) => (
        <div key={cat}>
          <div className="flex items-center gap-2 mb-2">
            <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${CAT_COLORS[cat] || "bg-gray-100 text-gray-700"}`}>
              {categoryLabel(cat, t)}
            </span>
          </div>
          <div className="space-y-2">
            {items.map((r) => (
              <Card key={r.id}>
                <CardContent className="py-3 px-4 flex items-start gap-3">
                  <BookOpen className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <p className="font-medium text-sm">{r.title}</p>
                    {r.description && <p className="text-xs text-muted-foreground mt-0.5">{r.description}</p>}
                  </div>
                  {r.url && (
                    <a href={r.url} target="_blank" rel="noopener noreferrer" className="text-primary hover:text-primary/80">
                      <ExternalLink className="h-4 w-4" />
                    </a>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Dashboard ─────────────────────────────────────────────────────────────────

function DashboardTab() {
  const { t } = useTranslation();
  const [overview, setOverview] = useState(null);
  const [stats, setStats] = useState(null);
  const [departments, setDepartments] = useState([]);
  const [deptId, setDeptId] = useState("all");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (dept = "all") => {
    setLoading(true);
    try {
      const url = dept && dept !== "all" ? `v1/wellbeing/team?department_id=${dept}` : "v1/wellbeing/team";
      const [ovRes, stRes, deptRes] = await Promise.all([
        apiClient.get(url).catch(() => ({ data: null })),
        apiClient.get("v1/wellbeing/stats").catch(() => ({ data: null })),
        apiClient.get("v1/organization-chart/departments/").catch(() => ({ data: [] })),
      ]);
      setOverview(ovRes.data);
      setStats(stRes.data);
      setDepartments(deptRes.data || []);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const scoreColor = (avg) => {
    if (!avg) return "text-muted-foreground";
    if (avg >= 4) return "text-green-600";
    if (avg >= 3) return "text-yellow-600";
    return "text-red-600";
  };

  const trendIcon = (avg) => {
    if (!avg) return <Minus className="h-4 w-4 text-muted-foreground" />;
    if (avg >= 4) return <TrendingUp className="h-4 w-4 text-green-500" />;
    if (avg >= 3) return <Minus className="h-4 w-4 text-yellow-500" />;
    return <TrendingDown className="h-4 w-4 text-red-500" />;
  };

  return (
    <div className="space-y-5">
      <div className="flex gap-3 items-center">
        <Select value={deptId} onValueChange={(v) => { setDeptId(v); load(v); }}>
          <SelectTrigger className="w-52">
            <SelectValue placeholder={t("wellbeingHub.dashboard.allDepartments")} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("wellbeingHub.dashboard.allDepartments")}</SelectItem>
            {departments.map((d) => (
              <SelectItem key={d.id} value={String(d.id)}>{d.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button variant="outline" size="sm" onClick={() => load(deptId)}>
          <RefreshCw className="h-3 w-3 mr-1" /> {t("wellbeingHub.dashboard.refresh")}
        </Button>
      </div>

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <MiniStat label={t("wellbeingHub.dashboard.statToday")} value={stats.checkins_today} />
          <MiniStat label={t("wellbeingHub.dashboard.statTotal")} value={stats.total_checkins} />
          <MiniStat label={t("wellbeingHub.dashboard.statChallenges")} value={stats.active_challenges} />
          <MiniStat label={t("wellbeingHub.dashboard.statResources")} value={stats.eap_resources} />
        </div>
      )}

      {loading ? (
        <p className="text-muted-foreground">{t("wellbeingHub.loading")}</p>
      ) : overview && overview.checkin_count > 0 ? (
        <>
          <p className="text-sm text-muted-foreground">
            {t("wellbeingHub.dashboard.basedOn", { count: overview.checkin_count })}
          </p>

          <div className="grid grid-cols-3 gap-4">
            {[
              { label: t("wellbeingHub.dashboard.avgMood"), value: overview.avg_mood, emojis: MOOD_EMOJIS },
              { label: t("wellbeingHub.dashboard.avgEnergy"), value: overview.avg_energy, emojis: ENERGY_EMOJIS },
              { label: t("wellbeingHub.dashboard.avgStress"), value: overview.avg_stress, emojis: STRESS_EMOJIS },
            ].map(({ label, value, emojis }) => (
              <Card key={label}>
                <CardContent className="py-4 text-center">
                  <p className="text-xs text-muted-foreground">{label}</p>
                  <p className={`text-3xl font-bold mt-1 ${scoreColor(value)}`}>{value ?? "—"}</p>
                  {value && <p className="text-xl mt-1">{emojis[Math.round(value)]}</p>}
                  <div className="flex justify-center mt-1">{trendIcon(value)}</div>
                </CardContent>
              </Card>
            ))}
          </div>

          {overview.at_risk_count > 0 && (
            <Card className="border-red-200 bg-red-50/50">
              <CardContent className="py-3 px-4 flex items-center gap-3">
                <AlertTriangle className="h-5 w-5 text-red-500 flex-shrink-0" />
                <div>
                  <p className="font-semibold text-sm text-red-700">
                    {t("wellbeingHub.dashboard.atRiskTitle", { count: overview.at_risk_count })}
                  </p>
                  <p className="text-xs text-red-600">
                    {t("wellbeingHub.dashboard.atRiskHint")}
                  </p>
                </div>
              </CardContent>
            </Card>
          )}

          {overview.mood_distribution && Object.keys(overview.mood_distribution).length > 0 && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">{t("wellbeingHub.dashboard.moodDistribution")}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {Object.entries(overview.mood_distribution)
                  .sort((a, b) => b[1] - a[1])
                  .map(([label, count]) => (
                    <div key={label} className="flex items-center gap-2">
                      <span className="text-xs w-28 text-muted-foreground">{label}</span>
                      <Progress value={(count / overview.checkin_count) * 100} className="h-2 flex-1" />
                      <span className="text-xs w-6 text-right">{count}</span>
                    </div>
                  ))}
              </CardContent>
            </Card>
          )}
        </>
      ) : (
        <Card>
          <CardContent className="py-10 text-center text-muted-foreground">
            <Activity className="h-8 w-8 mx-auto mb-2 opacity-30" />
            <p>{t("wellbeingHub.dashboard.empty")}</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function MiniStat({ label, value }) {
  return (
    <Card>
      <CardContent className="py-3 px-4">
        <p className="text-xs text-muted-foreground">{label}</p>
        <p className="text-2xl font-bold">{value ?? "—"}</p>
      </CardContent>
    </Card>
  );
}
